/**
 * Translation levels: Low · Medium · High · Advanced · Expert.
 *
 * The numbers come from the server's engine (GET /api/engine → `ai`), and the
 * price shown next to the picker is worked out HERE, as the person types, with a
 * copy of the engine's own quote (backend src/engine/ai.ts `quoteText`). A
 * request per keystroke would only be asking the server to do this same
 * arithmetic. It is an estimate either way — the charge is settled from what the
 * model really used, and the answer says what it cost.
 */

const API = process.env.NEXT_PUBLIC_API_BASE ?? '';

export interface AiLevel {
  key: string;
  name: string;
  blurb: string;
  inPerM: number;
  outPerM: number;
  cachedPerM: number;
  margin: number;
  minimum: number;
  briefTokens: number;
  review?: { inPerM: number; outPerM: number; cachedPerM: number; briefTokens: number };
}

export interface AiPricing {
  /** False when the engine's AI pricing is off: no levels, today's flat prices. */
  live: boolean;
  defaultLevel: string;
  guestLevel: string;
  maxPerRequest: number;
  overviewOut: number;
  kharmaPerUsd: number;
  levels: AiLevel[];
}

/**
 * The engine's AI pricing, or null when it has none (level pricing is off).
 * Throws when the server could not be asked — a caller re-reading it keeps what
 * it already has rather than taking a failed request for "pricing is off".
 */
export async function fetchAiPricing(): Promise<AiPricing | null> {
  const res = await fetch(`${API}/api/engine`, { credentials: 'include', cache: 'no-store' });
  if (!res.ok) throw new Error(`engine unavailable (${res.status})`);
  const body = await res.json().catch(() => null);
  const ai = body?.ai;
  if (!ai || !Array.isArray(ai.levels) || !ai.levels.length) return null;
  return ai as AiPricing;
}

/** The engine's token estimate: ~4 characters a token, ~1.5 for CJK. */
export function estimateTokens(text: string): number {
  if (!text) return 0;
  let cjk = 0;
  for (const ch of text) if (ch.codePointAt(0)! >= 0x2e80) cjk++;
  return Math.max(1, Math.ceil((text.length - cjk) / 4 + cjk / 1.5));
}

/** About what a text costs at a level (the engine's `quoteText`, the expected price). */
export function quoteLevel(p: AiPricing, l: AiLevel, text: string, overview: boolean): number {
  const tin = estimateTokens(text);
  const tout = Math.ceil(tin * 1.2) + 8;
  const extra = overview ? p.overviewOut : 0;
  let usd = (tin * l.inPerM + l.briefTokens * l.cachedPerM + (tout + extra) * l.outPerM) / 1e6;
  if (l.review) usd += ((tin + tout) * l.review.inPerM + l.review.briefTokens * l.review.cachedPerM + tout * l.review.outPerM) / 1e6;
  return Math.max(l.minimum, Math.ceil(usd * p.kharmaPerUsd * l.margin - 1e-9));
}

const STORE = 'gt.level';

/** The level this browser last chose. Storage can be missing or blocked; that is just "none". */
export function loadLevel(): string | null {
  try {
    return localStorage.getItem(STORE);
  } catch {
    return null;
  }
}

export function saveLevel(key: string): void {
  try {
    localStorage.setItem(STORE, key);
  } catch {
    /* a private window: the choice lasts for this page */
  }
}
